import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ScanFace, QrCode, Images, Sparkles, ShieldCheck, Zap, Search, Heart, Download,
  Users, Camera, Upload, ArrowRight, Check, Gauge, Lock, Star, HeartHandshake,
  Cake, Briefcase, PartyPopper, Play, ChevronRight,
} from "lucide-react";
import { Navbar } from "@/components/common/Navbar";
import { Footer } from "@/components/common/Footer";
import { Reveal } from "@/components/common/Reveal";
import { Counter } from "@/components/common/Counter";
import { HERO, COUPLES, GUESTS, DECOR } from "@/lib/media";
import { PLANS as FALLBACK, inr } from "@/lib/plans";
import api from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function FloatCard({ className, children, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`bg-white rounded-2xl px-4 py-3 shadow-xl shadow-maroon/10 border border-maroon/10 ${className}`}>
      {children}
    </motion.div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="w-8 h-px bg-gold" />
      <span className="text-xs uppercase tracking-[0.3em] text-maroon">{children}</span>
    </div>
  );
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-cream mandala pt-28 pb-16">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-marigold/10 to-transparent pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 mb-7 border border-maroon/10 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            <span className="text-xs tracking-[0.2em] uppercase text-ink/70">AI-powered galleries for photographers</span>
          </motion.div>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink font-medium">
            Your photos.<br />Their moments.<br /><span className="text-gradient-royal italic font-light">One effortless gallery.</span>
          </h1>

          <p className="text-ink/60 text-base sm:text-lg mt-7 max-w-xl leading-relaxed">
            PMR Events helps professional photographers deliver beautiful, AI-powered event galleries
            with face search and effortless photo selection — so your clients can relive every moment.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-9">
            <button data-testid="hero-get-started" onClick={() => navigate("/register")}
              className="group inline-flex items-center gap-2 bg-maroon text-cream font-medium rounded-full px-7 py-3.5 hover:bg-maroon-dark transition-colors maroon-glow">
              Create your gallery <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a href="#how" data-testid="hero-how-it-works"
              className="inline-flex items-center gap-2 text-ink font-medium hover:text-maroon transition-colors">
              <span className="w-11 h-11 rounded-full border border-maroon/30 flex items-center justify-center"><Play className="w-4 h-4 text-maroon fill-maroon ml-0.5" /></span>
              See how it works
            </a>
          </div>

          <div className="mt-12 text-xs uppercase tracking-[0.28em] text-ink/40">
            Trusted by photographers, loved by clients.
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }}
            className="relative rounded-[2rem] overflow-hidden border-4 border-white shadow-2xl shadow-maroon/20 aspect-[4/3]">
            <img src={COUPLES[1]} alt="Featured wedding" className="w-full h-full object-cover" />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
              className="absolute" style={{ top: "26%", left: "34%", width: "22%", height: "30%" }}>
              <div className="w-full h-full border-2 border-white rounded-lg shadow-lg" />
            </motion.div>
          </motion.div>

          <FloatCard className="absolute right-2 sm:-right-4 top-1/3 floaty" delay={0.7}>
            <div className="flex items-center gap-3">
              <img src={COUPLES[1]} alt="" className="w-9 h-9 rounded-full object-cover border border-gold/40" />
              <div>
                <div className="text-sm text-ink font-medium">Same person</div>
                <div className="text-[11px] text-maroon">42 photos found</div>
              </div>
              <ChevronRight className="w-4 h-4 text-ink/40" />
            </div>
          </FloatCard>

          <div className="absolute -bottom-4 right-6 font-serif italic text-maroon/70 text-lg rotate-[-4deg]">
            More than photos<br />Memories forever
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 inset-x-0 z-10 flex justify-center">
        <div className="flex items-center gap-3 text-ink/40 text-xs tracking-[0.3em] uppercase">
          <span>Upload</span><ArrowRight className="w-3 h-3" /><span>Scan</span><ArrowRight className="w-3 h-3" /><span>Find</span><ArrowRight className="w-3 h-3" /><span>Select</span>
        </div>
      </div>
    </section>
  );
}

function TrustedStrip() {
  const items = [
    [ScanFace, "Face Search", "Find every moment with a single face."],
    [Images, "Smart Selection", "Clients can easily select their favourite photos."],
    [Star, "Branded Galleries", "Your logo, your style, your client experience."],
  ];
  return (
    <section className="relative z-10 border-y border-maroon/10 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-maroon/10">
        {items.map(([Icon, title, desc]) => (
          <div key={title} className="flex items-center gap-4 py-4 md:py-2 md:px-8 first:md:pl-0">
            <div className="w-12 h-12 rounded-full bg-maroon/8 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-maroon" /></div>
            <div>
              <div className="text-ink font-medium">{title}</div>
              <div className="text-sm text-ink/55">{desc}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProblemSolution() {
  return (
    <section className="relative z-10 py-28 max-w-7xl mx-auto px-5 sm:px-8 bg-cream">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <SectionLabel>The old way is broken</SectionLabel>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium leading-tight">Thousands of photos.<br />Nobody can find themselves.</h2>
          <p className="text-ink/60 mt-6 leading-relaxed">Guests scroll endlessly through massive drives. Clients feel overwhelmed choosing an album. Photographers drown in manual sorting and “can you find my photos?” messages.</p>
          <ul className="mt-8 space-y-3">
            {["Endless unsorted folders", "Manual photo tagging", "Slow, clunky client galleries", "Chaotic album selection"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-ink/70"><span className="w-1.5 h-1.5 rounded-full bg-maroon" /> {t}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-xl shadow-maroon/10 aspect-[4/3]">
            <img src={GUESTS[0]} alt="Celebration" className="w-full h-full object-cover" />
            <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-5 border border-maroon/10">
              <div className="flex items-center gap-3 mb-2"><Sparkles className="w-4 h-4 text-gold" /><span className="text-sm text-ink font-medium">With PMR Events</span></div>
              <p className="text-xs text-ink/60">Guests upload one selfie and instantly discover every photo they're in. Selection takes minutes, not weeks.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Camera, n: "01", title: "Create Event", desc: "Add event name, client, date, type, venue and a cover image in a beautiful wizard.", chip: "Wedding · Rahul & Priya" },
    { icon: Upload, n: "02", title: "Upload Photos", desc: "Drag & drop thousands of photos. Watch AI index every face in real time.", chip: "1,284 uploaded · AI 78%" },
    { icon: QrCode, n: "03", title: "Share QR", desc: "Generate a unique QR & secure link. Print, WhatsApp or share instantly.", chip: "Scan to find your photos" },
    { icon: ScanFace, n: "04", title: "Clients Find Themselves", desc: "Guests upload a selfie and instantly see every moment they appear in.", chip: "427 photos found containing you" },
  ];
  return (
    <section id="how" className="relative z-10 py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center"><SectionLabel>How it works</SectionLabel></div>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium">Four steps to unforgettable</h2>
          <p className="text-ink/60 mt-5">From shoot to smiles — a workflow so simple, anyone can master it.</p>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="group relative h-full bg-cream rounded-2xl p-7 hover-lift border border-maroon/8 hover:border-gold">
                <div className="font-serif text-5xl text-maroon/15 group-hover:text-gold/50 transition-colors">{s.n}</div>
                <div className="w-11 h-11 rounded-xl bg-maroon/10 flex items-center justify-center -mt-4 mb-5"><s.icon className="w-5 h-5 text-maroon" /></div>
                <h3 className="text-lg text-ink font-medium">{s.title}</h3>
                <p className="text-sm text-ink/60 mt-2 leading-relaxed">{s.desc}</p>
                <div className="mt-5 text-[11px] text-maroon bg-white rounded-lg px-3 py-2 inline-block border border-maroon/10">{s.chip}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaceShowcase() {
  return (
    <section id="face" className="relative z-10 py-28 max-w-7xl mx-auto px-5 sm:px-8 bg-cream">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <div className="relative">
            <div className="grid grid-cols-3 gap-3">
              {[...COUPLES, ...GUESTS].slice(0, 9).map((src, i) => {
                const hit = i === 0 || i === 4 || i === 8;
                return (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                    className={`relative rounded-xl overflow-hidden aspect-square border-2 ${hit ? "border-gold ring-2 ring-gold/40" : "border-white"} shadow-sm`}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    {hit && <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-gold flex items-center justify-center"><Check className="w-3 h-3 text-white" strokeWidth={3} /></div>}
                  </motion.div>
                );
              })}
            </div>
            <FloatCard className="absolute -bottom-6 left-1/2 -translate-x-1/2 floaty">
              <div className="flex items-center gap-3"><ScanFace className="w-5 h-5 text-maroon" /><div className="text-sm text-ink font-medium">427 matching photos found</div></div>
            </FloatCard>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <SectionLabel>AI Face Search</SectionLabel>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium leading-tight">Find every moment<br />you were part of.</h2>
          <p className="text-ink/60 mt-6 leading-relaxed">Powered by real face-embedding vectors and cosine similarity search. Guests upload a single selfie and our engine surfaces every photograph containing a similar face — instantly.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {[[ScanFace, "Face embeddings", "512-d ArcFace vectors from every photo"], [Search, "Similarity search", "Cosine matching with a tunable threshold"], [Lock, "Private by design", "Raw face data encrypted, never exposed"], [Gauge, "Adjustable accuracy", "Tune sensitivity per event"]].map(([Icon, t, d]) => (
              <div key={t} className="bg-white rounded-xl p-4 border border-maroon/8 shadow-sm"><Icon className="w-5 h-5 text-gold mb-3" /><div className="text-ink text-sm font-medium">{t}</div><div className="text-xs text-ink/55 mt-1">{d}</div></div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DashboardShowcase() {
  return (
    <section className="relative z-10 py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center"><SectionLabel>Photographer Dashboard</SectionLabel></div>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium">A studio command centre</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-14 bg-cream rounded-3xl p-6 sm:p-8 border border-maroon/10 shadow-xl shadow-maroon/5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[["Total Events", 24], ["Active Events", 9], ["Total Photos", 18420], ["Photos Selected", 3670]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-maroon/10 p-5 bg-white"><div className="text-xs text-ink/50 uppercase tracking-wider">{l}</div><div className="font-serif text-3xl text-maroon mt-2"><Counter to={v} /></div></div>
              ))}
            </div>
            <div className="mt-5 rounded-xl border border-gold/40 p-6 bg-gradient-to-r from-marigold/10 to-transparent flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div><div className="text-ink font-medium">Wedding — Rahul & Priya</div><div className="text-sm text-ink/60 mt-1">1,842 Photos · 367 Selected · 89% completion</div></div>
              <div className="w-full sm:w-64"><div className="h-2 rounded-full bg-maroon/10 overflow-hidden"><div className="h-full bg-gold" style={{ width: "89%" }} /></div></div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function GalleryShowcase() {
  const imgs = [...COUPLES, ...GUESTS, ...DECOR, ...COUPLES.slice(0, 3)];
  return (
    <section className="relative z-10 py-28 max-w-7xl mx-auto px-5 sm:px-8 bg-cream">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <SectionLabel>Client Gallery</SectionLabel>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium leading-tight">A gallery clients<br />actually love.</h2>
          <p className="text-ink/60 mt-6 leading-relaxed">A Google-Photos-like experience with PMR branding: masonry layout, full-screen viewer, swipe on mobile, favourites and one-tap selection. Built mobile-first.</p>
          <div className="space-y-3 mt-8">
            {[[Images, "Masonry & fullscreen viewer"], [Heart, "Favourite & select in one tap"], [Download, "Download when enabled"], [Search, "Instant selfie face search"]].map(([Icon, t]) => (
              <div key={t} className="flex items-center gap-3 text-ink/70"><div className="w-8 h-8 rounded-lg bg-maroon/10 flex items-center justify-center"><Icon className="w-4 h-4 text-maroon" /></div>{t}</div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="masonry">
            {imgs.map((src, i) => (
              <div key={i} className="rounded-xl overflow-hidden border-2 border-white shadow-sm hover-lift"><img src={src} alt="" loading="lazy" className="w-full object-cover" /></div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Features() {
  const feats = [
    [Camera, "Event creation wizard", "Multi-step, elegant, effortless."],
    [Upload, "Batch photo upload", "Thousands of photos with live processing states."],
    [ScanFace, "AI face matching", "Real embeddings + cosine similarity."],
    [QrCode, "QR & secure links", "Printable cards & instant sharing."],
    [Images, "Google-Photos gallery", "Masonry, fullscreen, lazy-loaded."],
    [Heart, "Album selection", "Clients select & submit in minutes."],
    [Gauge, "Analytics", "Views, searches, selection completion."],
    [ShieldCheck, "Privacy & security", "Encrypted face data, consent-first."],
    [Users, "Team roles", "Admin, photographer & studio members."],
  ];
  return (
    <section id="features" className="relative z-10 py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center"><SectionLabel>Everything you need</SectionLabel></div>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium">A complete photography platform</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-16">
          {feats.map(([Icon, t, d], i) => (
            <Reveal key={t} delay={(i % 3) * 0.08}>
              <div className="group bg-cream rounded-2xl p-7 h-full hover-lift border border-maroon/8 hover:border-gold">
                <div className="w-11 h-11 rounded-xl bg-maroon/10 flex items-center justify-center mb-5 group-hover:bg-gold/20 transition-colors"><Icon className="w-5 h-5 text-maroon" /></div>
                <h3 className="text-ink font-medium">{t}</h3><p className="text-sm text-ink/60 mt-2">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  const cases = [[HeartHandshake, "Weddings"], [Star, "Engagements"], [Camera, "Pre-Wedding"], [Cake, "Birthdays"], [Briefcase, "Corporate"], [PartyPopper, "Receptions"]];
  return (
    <section className="relative z-10 py-24 max-w-7xl mx-auto px-5 sm:px-8 bg-cream">
      <Reveal className="text-center max-w-2xl mx-auto">
        <div className="flex justify-center"><SectionLabel>Made for every celebration</SectionLabel></div>
        <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium">One platform, every event</h2>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-14">
        {cases.map(([Icon, t], i) => (
          <Reveal key={t} delay={i * 0.06}>
            <div className="bg-white rounded-2xl p-6 text-center hover-lift border border-maroon/8 hover:border-gold"><Icon className="w-6 h-6 text-maroon mx-auto mb-3" /><div className="text-sm text-ink/70">{t}</div></div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(FALLBACK);
  useEffect(() => { api.get("/public/plans").then((r) => setPlans(r.data.plans)).catch(() => {}); }, []);
  return (
    <section id="pricing" className="relative z-10 py-28 max-w-7xl mx-auto px-5 sm:px-8 bg-white">
      <Reveal className="text-center max-w-2xl mx-auto">
        <div className="flex justify-center"><SectionLabel>Pricing</SectionLabel></div>
        <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium">Plans that grow with your studio</h2>
        <p className="text-ink/60 mt-5">Simple, transparent pricing in Indian Rupees.</p>
      </Reveal>
      <div className="grid lg:grid-cols-3 gap-6 mt-16 items-stretch">
        {plans.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <div className={`relative h-full rounded-2xl p-8 flex flex-col ${p.popular ? "border-2 border-gold bg-gradient-to-b from-marigold/10 to-white shadow-xl shadow-gold/20" : "bg-cream border border-maroon/10"}`}>
              {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-maroon text-cream text-xs font-medium px-4 py-1 rounded-full">Most Popular</div>}
              <div className="text-ink font-medium text-lg">{p.name}</div>
              <div className="text-sm text-ink/55 mt-1">{p.tagline}</div>
              <div className="mt-6 flex items-end gap-1"><span className="font-serif text-5xl text-maroon">{inr(p.price)}</span><span className="text-ink/50 mb-1.5">/{p.period === "month" ? "mo" : p.period}</span></div>
              <ul className="mt-7 space-y-3 flex-1">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-ink/70"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
              <button data-testid={`pricing-${p.id}`} onClick={() => navigate("/register")}
                className={`mt-8 w-full rounded-full py-3 font-medium transition-colors ${p.popular ? "bg-maroon text-cream hover:bg-maroon-dark" : "bg-white text-ink border border-maroon/15 hover:border-gold"}`}>
                Start with {p.name}
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    ["How does the AI face search work?", "We extract face embeddings (numeric vectors) from every uploaded photo. When a guest uploads a selfie, we compute its embedding and use cosine-similarity vector search to return every matching photograph above a configurable threshold."],
    ["Is my clients' face data private?", "Absolutely. Raw face embeddings are stored securely, never exposed publicly, and can be deleted at any time. Guests see a consent notice before any selfie is processed."],
    ["Can clients access galleries on mobile?", "Yes — the entire client experience is mobile-first: selfie capture, fullscreen swiping, favouriting and album selection all feel native on phones."],
    ["How do clients receive their gallery?", "Every event gets a unique QR code and secure link. Print it, share it on WhatsApp, or send the link directly."],
    ["Can I control downloads and watermarks?", "Yes. Each event has gallery privacy, download permissions, watermark and expiry controls."],
    ["Is the pricing final?", "Pricing is in INR and can be adjusted anytime by the PMR Events admin."],
  ];
  return (
    <section id="faq" className="relative z-10 py-28 bg-cream">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <div className="flex justify-center"><SectionLabel>FAQ</SectionLabel></div>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium">Questions, answered</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-12">
            {faqs.map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-maroon/12">
                <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-ink hover:text-maroon hover:no-underline font-normal text-base">{q}</AccordionTrigger>
                <AccordionContent className="text-ink/60 leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCTA() {
  const navigate = useNavigate();
  return (
    <section className="relative z-10 py-28 px-5 sm:px-8 bg-white">
      <div className="max-w-5xl mx-auto relative rounded-3xl overflow-hidden border-4 border-white shadow-2xl shadow-maroon/20">
        <img src={COUPLES[2]} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-maroon-dark/90 via-maroon-dark/70 to-maroon/50" />
        <div className="relative text-center px-6 py-20">
          <h2 className="font-serif text-4xl sm:text-6xl text-cream font-light leading-tight">Let your clients find themselves<br /><span className="text-gradient-gold italic">in every moment.</span></h2>
          <p className="text-cream/80 mt-6 max-w-xl mx-auto">Join photographers delivering smarter galleries with PMR Events.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-10">
            <button data-testid="cta-get-started" onClick={() => navigate("/register")} className="inline-flex items-center gap-2 bg-gold text-ink font-medium rounded-full px-8 py-3.5 hover:bg-gold-light transition-colors">Get Started <ArrowRight className="w-4 h-4" /></button>
            <Link to="/login" className="inline-flex items-center gap-2 bg-white/10 text-cream border border-cream/30 rounded-full px-8 py-3.5 hover:bg-white/20 transition-colors">Sign in</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const quotes = [
    { q: "My couples used to wait weeks to shortlist an album. Now they finish selection the same evening the gallery goes live. Game changer.", n: "Aditya Rao", s: "Aditya Rao Photography, Bengaluru", img: GUESTS[1] },
    { q: "The face search genuinely stunned my clients — a guest uploaded one selfie and found all 60 photos she was in. They kept sharing it with family.", n: "Simran Kaur", s: "Studio Saffron, Chandigarh", img: COUPLES[4] },
    { q: "I run a 6-person studio. Branded galleries and per-event QR codes made us look far bigger than we are. Bookings are up.", n: "Farhan Sheikh", s: "Sheikh Films, Hyderabad", img: DECOR[0] },
  ];
  return (
    <section className="relative z-10 py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center"><SectionLabel>Loved by studios</SectionLabel></div>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink font-medium">Photographers don't go back</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {quotes.map((t, i) => (
            <Reveal key={t.n} delay={i * 0.12}>
              <div className="h-full bg-cream rounded-2xl p-7 border border-maroon/10 hover-lift">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, k) => <Star key={k} className="w-4 h-4 text-gold fill-gold" />)}</div>
                <p className="text-ink/75 leading-relaxed font-serif text-lg italic">“{t.q}”</p>
                <div className="flex items-center gap-3 mt-6 pt-5 border-t border-maroon/10">
                  <img src={t.img} alt="" className="w-11 h-11 rounded-full object-cover border border-gold/30" />
                  <div><div className="text-ink font-medium text-sm">{t.n}</div><div className="text-xs text-ink/50">{t.s}</div></div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="relative bg-cream overflow-hidden">
      <Navbar />
      <Hero />
      <TrustedStrip />
      <ProblemSolution />
      <HowItWorks />
      <FaceShowcase />
      <DashboardShowcase />
      <GalleryShowcase />
      <Features />
      <UseCases />
      <Testimonials />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
